/**
 * Helper to load an image from a File object into an HTMLImageElement using Object URLs.
 */
const loadImage = (file) => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
};

/**
 * Helper to convert canvas content to a Blob.
 */
const canvasToBlob = (canvas, format, quality) => {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, format, quality);
  });
};

/**
 * Optimizes a product image:
 * - Resizes the longest side to 1600px, maintaining aspect ratio.
 * - Converts image to WebP format.
 * - Adaptive compression from quality 0.85 down to 0.70 until size <= 700KB.
 */
export const optimizeProductImage = async (file) => {
  try {
    const img = await loadImage(file);
    let { width, height } = img;
    const maxDimension = 1600;

    if (width > height) {
      if (width > maxDimension) {
        height = Math.round((height * maxDimension) / width);
        width = maxDimension;
      }
    } else {
      if (height > maxDimension) {
        width = Math.round((width * maxDimension) / height);
        height = maxDimension;
      }
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);

    let quality = 0.85;
    let blob = await canvasToBlob(canvas, 'image/webp', quality);

    // Adaptive compression loop
    while (blob.size > 700 * 1024 && quality > 0.70) {
      quality = Math.max(0.70, quality - 0.05);
      blob = await canvasToBlob(canvas, 'image/webp', quality);
    }

    const originalName = file.name;
    const lastDotIndex = originalName.lastIndexOf('.');
    const baseName = lastDotIndex !== -1 ? originalName.substring(0, lastDotIndex) : originalName;
    const newName = `${baseName}.webp`;

    return new File([blob], newName, {
      type: 'image/webp',
      lastModified: Date.now()
    });
  } catch (err) {
    console.error('Error optimizing product image:', err);
    throw err;
  }
};

/**
 * Optimizes a profile image:
 * - Extracts a center square crop.
 * - Resizes to 500x500 pixels.
 * - Converts image to WebP.
 * - Applies a constant 0.85 compression quality.
 */
export const optimizeProfileImage = async (file) => {
  try {
    const img = await loadImage(file);
    const { width, height } = img;
    const size = Math.min(width, height);
    const startX = (width - size) / 2;
    const startY = (height - size) / 2;

    const canvas = document.createElement('canvas');
    canvas.width = 500;
    canvas.height = 500;
    const ctx = canvas.getContext('2d');
    
    // Draw the cropped center square to the canvas resized to 500x500
    ctx.drawImage(img, startX, startY, size, size, 0, 0, 500, 500);

    const blob = await canvasToBlob(canvas, 'image/webp', 0.85);

    const originalName = file.name;
    const lastDotIndex = originalName.lastIndexOf('.');
    const baseName = lastDotIndex !== -1 ? originalName.substring(0, lastDotIndex) : originalName;
    const newName = `${baseName}.webp`;

    return new File([blob], newName, {
      type: 'image/webp',
      lastModified: Date.now()
    });
  } catch (err) {
    console.error('Error optimizing profile image:', err);
    throw err;
  }
};

/**
 * Prepares a bill / invoice image:
 * - Preserves original quality and format.
 * - No resizing or format conversion.
 */
export const prepareBillImage = async (file) => {
  return file;
};
